import type { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import type { TrackedTranslation } from '@ps/web-core-intl';
import type { History } from 'history';

import type { MultiPurposeNotification } from '@swf/core/types/Notification';

import type { ActionType, FormatType } from '../enums';
import { NotificationType } from '../enums';

import BonusCleared from './Bonus/Bonus.Cleared';
import BonusExpired from './Bonus/Bonus.Expired';
import BonusIssued from './Bonus/Bonus.Issued';
import ChestRewardAvailable from './ChestReward/ChestReward.available.config';
import ChestRewardIceLocked from './ChestReward/ChestReward.icelocked.config';
import ChestRewardMultiple from './ChestReward/ChestReward.multiple.config';
import ChestRewardTimeLocked from './ChestReward/ChestReward.timelocked.config';
import NotificationErrorConfig from './Error/NotificationError';
import FixedPrizeDrawIssued from './FixedPrizeDraw/FixedPrizeDraw.Issued';
import FixedPrizeDrawLost from './FixedPrizeDraw/FixedPrizeDraw.Lost';
import FixedPrizeDrawWon from './FixedPrizeDraw/FixedPrizeDraw.Won';
import FreeSpinsExpired from './FreeSpins/FreeSpins.Expired';
import FreeSpinsIssued from './FreeSpins/FreeSpins.Issued';
import MiniGameTicketConfig from './GameTicket/MiniGameTicket';
import InstantBonusSplitWager from './InstantBonus/InstantBonus.SplitWager';

export interface CardTranslationTag {
  tag: TrackedTranslation;
  values: { [key: number]: string };
}

export interface CardAttributes {
  attrKey: string;
  attrLabel: CardTranslationTag;
  attrFormatterType: FormatType;
  attrFormatKey?: string;
}

export interface CardAction {
  actionType: ActionType;
  actionLabel: CardTranslationTag;
  actionFunction: (params: ActionParams) => object;
  href?: string;
}

export interface ActionParams {
  attributes?: MultiPurposeNotification['attributes'];
  notificationId?: number;
  notificationType?: NotificationType;
  history: History;
}

export interface ViewConfig {
  title: CardTranslationTag;
  description?: CardTranslationTag;
  moreInfoLabel?: CardTranslationTag;
  defaultHeaderImageURL?: string;
  showCloseIcon: boolean;
  displayAttributes?: CardAttributes[];
  actions?: CardAction[];
}

export interface CardViewConfig {
  view: React.FC<CardViewProps>;
  viewConfig: ViewConfig;
}

export interface C2LEventData {
  data: { c2l: string };
}

export interface CardConfig {
  cardData: MultiPurposeNotification;
  front: CardViewConfig;
  back: CardViewConfig;
  showBack: boolean;
  locale: string;
  license: LicenceId;
  onToggleFlip(): void;
  onClickC2L(evt: C2LEventData): void;
  onClickClose(): void;
  onClickPrimaryAction(): void;
  onClickSecondaryAction(): void;
}

export interface CardViewProps {
  viewConfig: ViewConfig;
  cardData: MultiPurposeNotification;
  locale: string;
  license: LicenceId;
  onToggleFlip(): void;
  onClickClose(): void;
  onClickPrimaryAction(): void;
  onClickSecondaryAction(): void;
  onClickC2L(evt: C2LEventData): void;
}

export default {
  [NotificationType.BonusCleared]: BonusCleared,
  [NotificationType.BonusExpiring]: BonusExpired,
  [NotificationType.BonusIssued]: BonusIssued,
  [NotificationType.ChestRewardAvailable]: ChestRewardAvailable,
  [NotificationType.ChestRewardIceLocked]: ChestRewardIceLocked,
  [NotificationType.ChestRewardMultiple]: ChestRewardMultiple,
  [NotificationType.ChestRewardTimeLocked]: ChestRewardTimeLocked,
  [NotificationType.FixedPrizeDrawIssued]: FixedPrizeDrawIssued,
  [NotificationType.FixedPrizeDrawLost]: FixedPrizeDrawLost,
  [NotificationType.FixedPrizeDrawWon]: FixedPrizeDrawWon,
  [NotificationType.FreeSpinsExpiring]: FreeSpinsExpired,
  [NotificationType.FreeSpinsIssued]: FreeSpinsIssued,
  [NotificationType.InstantBonusSplitWager]: InstantBonusSplitWager,
  [NotificationType.MiniGameTicket]: MiniGameTicketConfig,
  [NotificationType.NotificationError]: NotificationErrorConfig
};
